import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Row, Space, Table } from "antd";
import { PageHeader } from "components/pageHeader";
import { useDeleteDeskMutation, useFetchDesksQuery } from "features/desksTable/desksAPI";
import DeskModal from "features/desksTable/addDeskModal";
import { getColumnSearchProps } from "common/antd-utils";
import { PERMISSIONS } from "features/permissions/constants";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import {
  useFetchRolesQuery,
  useFetchUserSummariesQuery,
} from "features/userManagementTable/userManagementAPI";
import { useRef, useState } from "react";
import styled from "styled-components";
import moment from "moment-timezone";

const StyledHeader = styled(PageHeader)`
  margin-bottom: 0;
`;

const StyledRow = styled(Row)`
  margin-bottom: 8px;
`;

export default function DesksTable() {
  const {
    data: {
      [PERMISSIONS.DESK__GET]: isDeskGetAuthorized,
      [PERMISSIONS.DESK__CREATE]: isDeskCreateAuthorized,
      [PERMISSIONS.DESK__UPDATE]: isDeskUpdateAuthorized,
      [PERMISSIONS.DESK__DELETE]: isDeskDeleteAuthorized,
    },
  } = useAuthorizations();

  const [isModalVisible, setModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const { data: desks = [], isLoading } = useFetchDesksQuery({
    skip: !isDeskGetAuthorized,
  });

  // Add roles and users queries
  const { data: roles } = useFetchRolesQuery();
  const { data: users } = useFetchUserSummariesQuery({
    isActive: true,
  });

  const [deleteDesk] = useDeleteDeskMutation();

  const onAddNewDesk = async () => {
    setActiveRecord(null);
    setModalVisible(true);
  };

  const onCloseModal = async () => {
    setActiveRecord(null);
    setModalVisible(false);
  };

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => text ?? "-",
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => text ?? "-",
      ...getColumnSearchProps("description", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.description ?? null,
      sorter: (a, b) => a.description.localeCompare(b.description),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Desk Access",
      render: (text, record) => {
        const roleNames =
          record.groupIds
            ?.map((groupId) => {
              const role = roles?.find((r) => r.id === groupId);
              return role?.name;
            })
            .filter(Boolean) || [];

        const userNames =
          record.userIds
            ?.map((userId) => {
              const user = users?.find((u) => u.id === userId);
              return user?.name || user?.email || user?.id;
            })
            .filter(Boolean) || [];

        return (
          <Space direction="vertical" size="small">
            {roleNames.length > 0 && (
              <div>
                <strong>Roles:</strong> {roleNames.join(", ")}
              </div>
            )}
            {userNames.length > 0 && (
              <div>
                <strong>Users:</strong> {userNames.join(", ")}
              </div>
            )}
            {roleNames.length === 0 && userNames.length === 0 && (
              <span style={{ color: "#999" }}>No access assigned</span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("MM/DD/YYYY") ?? "-",
    },
    {
      title: "Actions",
      fixed: "right",
      key: "action",
      hidden: !isDeskUpdateAuthorized && !isDeskDeleteAuthorized,
      render: (_, record) => (
        <Space size="middle">
          {isDeskUpdateAuthorized && (
            <EditOutlined
              key="edit"
              onClick={() => {
                setActiveRecord(record);
                setModalVisible(true);
              }}
            />
          )}
          {isDeskDeleteAuthorized && (
            <Popconfirm
              placement="topLeft"
              okText="Yes"
              title="Are you sure you want to delete this desk?"
              onConfirm={() => {
                deleteDesk({ id: record.id });
              }}
            >
              <DeleteOutlined key="delete" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ].filter((column) => !column.hidden);

  return (
    <>
      <StyledHeader>Desks Management</StyledHeader>
      <StyledRow align="middle">
        <span>Manage desks and their configurations.</span>
        {isDeskCreateAuthorized && (
          <Button onClick={onAddNewDesk} icon={<PlusOutlined />} type="link">
            Add Desk
          </Button>
        )}
      </StyledRow>
      <Table
        loading={isLoading}
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={desks}
        onChange={handleChange}
      />
      {isModalVisible && (
        <DeskModal
          record={activeRecord}
          open={isModalVisible}
          onOk={onCloseModal}
          onCancel={onCloseModal}
        />
      )}
    </>
  );
}
